import {XIcon} from '@primer/octicons-react'
import {IconButton, Portal, PortalContext, registerPortalRoot, useFocusTrap, useOnEscapePress} from '@primer/react'
import {useCallback, useEffect, useId, useMemo, useRef, useState} from 'react'

import {BUTTON_LABELS} from '../constants/buttons'
import styles from './IssueViewer.module.css'

const DRAWER_TITLE = 'Details'

// Primer wraps each portaled subtree in a `z-index: 1` div, so the drawer and the side panel
// (both in `__primerPortalRoot__`) share a stacking level and the panel's sticky header can
// paint over the drawer. Lift the drawer's wrapper above the panel's. Keep in sync with the
// `.metadataDrawerBackdrop` z-index in IssueViewer.module.css.
const DRAWER_PORTAL_Z_INDEX = '11'

function findScrollableAncestor(element: HTMLElement | null): HTMLElement | null {
  const node = element?.parentElement
  if (!node) return null
  const {overflowY} = getComputedStyle(node)
  if (overflowY === 'auto' || overflowY === 'scroll') {
    return node
  }
  return findScrollableAncestor(node)
}

/**
 * Metadata drawer for when the issue opens inside a side panel with the sticky header on.
 * Unlike the full-viewport Primer `Dialog`, it stays scoped to the sheet: a `position: fixed`
 * overlay portaled into Primer's global root (outside the panel's transformed containing block,
 * so `fixed` pins to the viewport) and sized to the sheet's scroll-container rect. The panel is
 * itself `position: fixed`, so that rect is stable and the drawer never scrolls out of reach.
 */
export function SidePanelMetadataDrawer({
  onClose,
  returnFocusRef,
  children,
}: {
  onClose: () => void
  returnFocusRef: React.RefObject<HTMLElement | null>
  children: React.ReactNode
}) {
  const anchorRef = useRef<HTMLSpanElement>(null)
  const backdropRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLElement | null>(null)
  const titleId = useId()
  // Per-instance portal-root name registered on the drawer's fixed backdrop. The metadata
  // pickers (Primer SelectPanel) anchor `left` via CSS anchor(); by default they portal into
  // the global `__primerPortalRoot__`, which doesn't contain the trigger, so anchor() is
  // rejected and `left` collapses to 0 (overlay jumps to the viewport edge). Scoping them
  // (via PortalContext below) into this backdrop — an ancestor of the triggers — fixes that.
  const overlayPortalContainerName = useId()
  const [portalReady, setPortalReady] = useState(false)
  // Gate the picker-overlay PortalContext on the backdrop's portal root being registered: a
  // descendant Portal registers in a layout effect that runs before this ancestor's ref
  // callback, so rendering children first would throw "container not yet registered". The
  // extra commit is imperceptible behind the open animation.
  const [overlayRootReady, setOverlayRootReady] = useState(false)

  useFocusTrap({containerRef: panelRef, returnFocusRef}, [portalReady])
  useOnEscapePress(() => onClose())

  // Stable PortalContext value so descendant Portals don't re-read context every render.
  const overlayPortalContext = useMemo(
    () => ({portalContainerName: overlayPortalContainerName}),
    [overlayPortalContainerName],
  )

  // Register the fixed backdrop as a Primer portal root as soon as it mounts, so picker
  // overlays scoped via PortalContext (below) can mount into it before any picker opens.
  const setBackdropRef = useCallback(
    (node: HTMLDivElement | null) => {
      backdropRef.current = node
      if (node) {
        registerPortalRoot(node, overlayPortalContainerName)
        setOverlayRootReady(true)
        // Lift Primer's `z-index: 1` Portal wrapper above the side panel's own wrapper so the
        // drawer overlays the sheet (including its sticky header) instead of rendering behind it.
        // The wrapper is the backdrop's parent; the guard is defensive against an unexpected mount.
        const portalWrapper = node.parentElement
        if (portalWrapper && portalWrapper !== scrollContainerRef.current) {
          portalWrapper.style.zIndex = DRAWER_PORTAL_Z_INDEX
        }
      }
    },
    [overlayPortalContainerName],
  )

  // Resolve the side panel's scroll container from an inline anchor so we can size the overlay
  // to it and lock its scroll while the drawer is open. Walking the DOM from the anchor isn't
  // possible during render / SSR, so this runs in an effect; it also gates the Portal mount.
  useEffect(() => {
    scrollContainerRef.current = findScrollableAncestor(anchorRef.current)
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPortalReady(true)
  }, [])

  // Once the overlay is mounted, size its fixed backdrop to the scroll container's viewport
  // rect and keep it in sync on resize, and lock the container's scroll so the issue content
  // can't move behind the drawer.
  useEffect(() => {
    if (!portalReady) return
    const container = scrollContainerRef.current
    const measureTarget = container ?? document.documentElement
    const update = () => {
      const backdrop = backdropRef.current
      if (!backdrop) return
      const {top, left, width, height} = measureTarget.getBoundingClientRect()
      // Size the fixed backdrop to the panel's viewport rect (portaled into Primer's global root,
      // outside the panel's transformed containing block, so `fixed` resolves to the viewport).
      // The panel is itself `position: fixed`, so the rect is stable as the issue content scrolls.
      backdrop.style.top = `${top}px`
      backdrop.style.left = `${left}px`
      backdrop.style.width = `${width}px`
      backdrop.style.height = `${height}px`
    }
    update()
    const observer = new ResizeObserver(update)
    observer.observe(measureTarget)

    const previousOverflow = container?.style.overflow
    if (container) container.style.overflow = 'hidden'

    return () => {
      observer.disconnect()
      if (container) container.style.overflow = previousOverflow ?? ''
    }
  }, [portalReady])

  return (
    <>
      <span ref={anchorRef} hidden aria-hidden="true" />
      {portalReady && (
        <Portal>
          <div ref={setBackdropRef} className={styles.metadataDrawerBackdrop}>
            <div
              ref={panelRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby={titleId}
              className={styles.metadataDrawerPanel}
            >
              <div className={styles.metadataDrawerHeader}>
                <span id={titleId} className={styles.metadataDrawerTitle}>
                  {DRAWER_TITLE}
                </span>
                <IconButton
                  icon={XIcon}
                  aria-label={BUTTON_LABELS.closePanel}
                  variant="invisible"
                  size="small"
                  onClick={onClose}
                  // The focus trap gives this button initial focus on open; the attribute makes
                  // it focus with {preventScroll: true} so the panel doesn't scroll it into view.
                  // See @primer/behaviors focus-trap `focusWithDataOpt`.
                  data-prevent-scroll-on-focus
                />
              </div>
              <div className={styles.metadataDrawerBody}>
                {/* Scope picker overlays into the fixed backdrop (registered above) so their
                    CSS anchor positioning resolves against the trigger instead of collapsing
                    to the viewport's top-left. Only descendant Portals without an explicit
                    containerName pick this up; the drawer's own Portal is unaffected. */}
                {overlayRootReady && <PortalContext value={overlayPortalContext}>{children}</PortalContext>}
              </div>
            </div>
          </div>
        </Portal>
      )}
    </>
  )
}
